import { OutcomeItem } from "@/services/financeiro";
import {
  UnitsByCCItem,
  getTotalInvestidoPlanoPrefix,
  TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED,
} from "@/services/gerencial";
import {
  buildAdmObraBuckets,
  buildObraBuckets,
  type TotalInvestidoPlanoBucket,
} from "./gerencialPlanoBuckets";

export type { TotalInvestidoPlanoBucket };

/* ─── Types de saída ─────────────────────────────────────────────────────── */

export interface CustoUnitarioCCStat {
  costCenterId:   string;
  name:           string;
  totalCost:      number;
  unitCount:      number;
  custoUnitario:  number;
  items:          OutcomeItem[];
}

/** Custo agregado por unidade construtiva (etapa do orçamento), como no módulo Orçamento. */
export interface CustoPorUnidadeConstrutivaStat {
  buildingUnitKey: string;
  name:            string;
  totalCost:       number;
  parcelCount:     number;
  items:           OutcomeItem[];
}

export interface CustoMensalStat {
  mes:            string;
  mesKey:         string;
  custo:          number;
  custoAcum:      number;
  custoUnitario:  number;
  custoUnitAcum:  number;
}

export interface CustoMensalPorCCStat {
  mes:     string;
  mesKey:  string;
  [ccName: string]: number | string;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const MES_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                    "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function getMonthLabel(dateStr: string): string {
  if (!dateStr || dateStr.length < 7) return "—";
  const [yearStr, monthStr] = dateStr.substring(0, 7).split("-");
  const year  = (yearStr ?? "????").slice(2);
  const month = parseInt(monthStr ?? "0", 10) - 1;
  return `${MES_LABELS[month] ?? "??"}/${year}`;
}

function getMonthKey(dateStr: string): string {
  return dateStr?.substring(0, 7) ?? "";
}

function getEffectiveDate(item: OutcomeItem): string {
  return item.dueDate || item.issueDate;
}

/* ─── Computações ────────────────────────────────────────────────────────── */

/**
 * Calcula custo total, nº de unidades e custo unitário por centro de custo.
 * Usa unitsByCC para obter a contagem de casas; faz fallback se o CC não tiver unidades mapeadas.
 */
export function computeCustoUnitarioPorCC(
  outcome:    OutcomeItem[],
  unitsByCC:  UnitsByCCItem[],
  ccNameMap?: Map<string, string>
): CustoUnitarioCCStat[] {
  /* Mapa unitsByCC: costCenterId → count */
  const unitsMap = new Map<string, number>();
  for (const u of unitsByCC) {
    unitsMap.set(String(u.costCenterId).trim(), u.unitCount);
  }

  const map = new Map<string, CustoUnitarioCCStat>();

  for (const item of outcome) {
    const ccId = item.costCenterId || "sem-cc";
    const resolvedName =
      ccNameMap?.get(String(ccId).trim()) ||
      item.costCenterName ||
      item.costCenterId ||
      "Sem Centro de Custo";

    if (!map.has(ccId)) {
      const unitCount = unitsMap.get(ccId) ?? 0;
      map.set(ccId, {
        costCenterId:  ccId,
        name:          resolvedName,
        totalCost:     0,
        unitCount,
        custoUnitario: 0,
        items:         [],
      });
    }
    const s = map.get(ccId)!;
    s.totalCost += item.originalAmount;
    s.items.push(item);
  }

  /* Recalcula custo unitário */
  for (const s of map.values()) {
    s.custoUnitario = s.unitCount > 0 ? s.totalCost / s.unitCount : 0;
  }

  return Array.from(map.values())
    .filter((s) => s.totalCost > 0)
    .sort((a, b) => b.custoUnitario - a.custoUnitario);
}

/**
 * Agrega despesas por unidade construtiva (Sienge: building unit), alinhado ao Orçamento.
 * Parcelas sem U.C. entram em "Sem unidade construtiva no Sienge".
 */
export function computeCustoPorUnidadeConstrutiva(
  outcome: OutcomeItem[]
): CustoPorUnidadeConstrutivaStat[] {
  const map = new Map<string, CustoPorUnidadeConstrutivaStat>();

  for (const item of outcome) {
    const id = (item.buildingUnitId ?? "").trim();
    const nm = (item.buildingUnitName ?? "").trim();

    let key: string;
    let displayName: string;
    if (id) {
      key = `id:${id}`;
      displayName = nm || `Unidade ${id}`;
    } else if (nm) {
      key = `name:${nm}`;
      displayName = nm;
    } else {
      key = "__sem_uc__";
      displayName = "Sem unidade construtiva no Sienge";
    }

    if (!map.has(key)) {
      map.set(key, {
        buildingUnitKey: key,
        name:            displayName,
        totalCost:       0,
        parcelCount:     0,
        items:           [],
      });
    }
    const s = map.get(key)!;
    s.totalCost   += item.originalAmount;
    s.parcelCount += 1;
    s.items.push(item);
  }

  return Array.from(map.values())
    .filter((s) => s.totalCost > 0)
    .sort((a, b) => b.totalCost - a.totalCost);
}

/**
 * Calcula custos mensais acumulados para um único centro de custo.
 * Retorna custo mensal, custo acumulado, custo unitário mensal e acumulado.
 */
export function computeCustoAcumulado(
  outcome:   OutcomeItem[],
  unitCount: number
): CustoMensalStat[] {
  const map = new Map<string, { mes: string; custo: number }>();

  for (const item of outcome) {
    const date = getEffectiveDate(item);
    const key  = getMonthKey(date);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, { mes: getMonthLabel(date), custo: 0 });
    }
    map.get(key)!.custo += item.originalAmount;
  }

  const sorted = Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([mesKey, v]) => ({ mesKey, ...v }));

  let acum = 0;
  return sorted.map(({ mesKey, mes, custo }) => {
    acum += custo;
    return {
      mes,
      mesKey,
      custo,
      custoAcum:     acum,
      custoUnitario: unitCount > 0 ? custo / unitCount   : 0,
      custoUnitAcum: unitCount > 0 ? acum  / unitCount   : 0,
    };
  });
}

/**
 * Calcula evolução mensal de custos por múltiplos centros de custo.
 * Cada entrada do array tem o mês e uma propriedade por CC com o custo acumulado.
 */
export function computeCustoMensalPorCC(
  outcome:   OutcomeItem[],
  ccStats:   CustoUnitarioCCStat[],
  unitsByCC: UnitsByCCItem[]
): CustoMensalPorCCStat[] {
  if (ccStats.length === 0) return [];

  const unitsMap = new Map<string, number>();
  for (const u of unitsByCC) unitsMap.set(String(u.costCenterId).trim(), u.unitCount);

  /* Coleta todos os meses disponíveis */
  const allMonths = new Set<string>();
  for (const item of outcome) {
    const key = getMonthKey(getEffectiveDate(item));
    if (key) allMonths.add(key);
  }

  const sortedMonths = Array.from(allMonths).sort();

  /* Agrupa custo mensal por (mes, ccId) */
  type MonthCC = { [ccId: string]: number };
  const grid = new Map<string, MonthCC>();
  for (const m of sortedMonths) grid.set(m, {});

  for (const item of outcome) {
    const key = getMonthKey(getEffectiveDate(item));
    if (!key || !grid.has(key)) continue;
    const ccId = item.costCenterId || "sem-cc";
    const row  = grid.get(key)!;
    row[ccId]  = (row[ccId] ?? 0) + item.originalAmount;
  }

  /* Acumula por CC ao longo do tempo */
  const cumByCC = new Map<string, number>();
  for (const cc of ccStats) cumByCC.set(cc.costCenterId, 0);

  return sortedMonths.map((mesKey) => {
    const row   = grid.get(mesKey) ?? {};
    const label = getMonthLabel(mesKey + "-01");
    const entry: CustoMensalPorCCStat = { mes: label, mesKey };

    for (const cc of ccStats) {
      const custo = row[cc.costCenterId] ?? 0;
      cumByCC.set(cc.costCenterId, (cumByCC.get(cc.costCenterId) ?? 0) + custo);
      const acum      = cumByCC.get(cc.costCenterId) ?? 0;
      const unitCount = unitsMap.get(cc.costCenterId) ?? cc.unitCount;
      /* Custo unitário acumulado para o CC */
      entry[cc.name] = unitCount > 0 ? acum / unitCount : acum;
    }

    return entry;
  });
}

/**
 * KPIs gerais do módulo Gerencial para o conjunto de CCs filtrados.
 */
export function computeGerencialKpis(stats: CustoUnitarioCCStat[]) {
  const totalCost   = stats.reduce((s, c) => s + c.totalCost, 0);
  const totalUnits  = stats.reduce((s, c) => s + c.unitCount, 0);
  const avgUnitCost = totalUnits > 0 ? totalCost / totalUnits : 0;

  return { totalCost, totalUnits, avgUnitCost, ccCount: stats.length };
}

/**
 * Agrega despesas do outcome pelo plano financeiro (categoria / conta analítica).
 * Usado no modal "Total investido" no Gerencial.
 */
export interface FinancialPlanBreakdownRow {
  /** Código do plano (Sienge) — exibe no modal */
  accountCode:   string;
  /** ID da categoria no Sienge, quando existir */
  accountId: string | null;
  /** Nome vindo do bulk ou placeholder */
  label: string;
  total:         number;
  parcelCount:   number;
  pctOfGrand:    number;
  /** Parcelas (contas a pagar) agregadas neste plano — drill-down no modal */
  items:         OutcomeItem[];
}

/** Parcelas do outcome cujo centro de custo coincide com o da obra (ids numéricos normalizados). */
export function filterOutcomeByCostCenter(outcome: OutcomeItem[], ccId: string): OutcomeItem[] {
  const want = String(ccId).trim();
  if (!want) return [];
  return outcome.filter((item) => {
    const a = String(item.costCenterId ?? "").trim();
    if (!a || a === "sem-cc") return false;
    if (a === want) return true;
    if (/^\d+$/.test(a) && /^\d+$/.test(want) && Number(a) === Number(want)) return true;
    return false;
  });
}

function lookupPaymentCategoryCode(
  id: string | null,
  categoryCodesById: Map<string, string>
): string {
  if (!id) return "";
  const t = String(id).trim();
  let c = categoryCodesById.get(t) ?? "";
  if (c) return c;
  if (/^\d+$/.test(t)) {
    c = categoryCodesById.get(String(Number(t))) ?? "";
  }
  return c;
}

export function aggregateOutcomeByFinancialPlan(
  outcome: OutcomeItem[],
  categoryCodesById: Map<string, string>
): FinancialPlanBreakdownRow[] {
  const map = new Map<
    string,
    {
      label: string;
      total: number;
      parcelCount: number;
      accountId: string | null;
      accountCode: string;
      items: OutcomeItem[];
    }
  >();

  let grand = 0;
  for (const item of outcome) {
    grand += item.originalAmount;
    const id = item.accountId?.trim() ? String(item.accountId).trim() : null;
    const key = id ?? `name:${(item.accountName || "Sem classificação").trim()}`;
    const label =
      item.accountName?.trim() ||
      (id ? `Plano ${id}` : "Sem classificação");
    const codeFromItem = item.accountCode?.trim() || "";
    const codeFromApi = lookupPaymentCategoryCode(id, categoryCodesById);
    const accountCode = codeFromItem || codeFromApi || (id ?? "") || "";

    if (!map.has(key)) {
      map.set(key, {
        label,
        total: 0,
        parcelCount: 0,
        accountId: id,
        accountCode,
        items: [],
      });
    }
    const r = map.get(key)!;
    r.total += item.originalAmount;
    r.parcelCount += 1;
    r.items.push(item);
    if (!r.accountCode && accountCode) r.accountCode = accountCode;
  }

  const rows: FinancialPlanBreakdownRow[] = Array.from(map.values()).map((v) => ({
    accountCode: v.accountCode,
    accountId:   v.accountId,
    label:       v.label,
    total:       v.total,
    parcelCount: v.parcelCount,
    pctOfGrand:  grand > 0 ? (v.total / grand) * 100 : 0,
    items:       [...v.items].sort((a, b) => {
      const ad = a.paymentDate || a.dueDate || a.issueDate || "";
      const bd = b.paymentDate || b.dueDate || b.issueDate || "";
      const d = bd.localeCompare(ad);
      if (d !== 0) return d;
      return b.originalAmount - a.originalAmount;
    }),
  }));

  return rows.sort((a, b) => b.total - a.total);
}

/** Conta «mãe» (capítulo 210, 211, …) — 210/211 com buckets da planilha; 207/201/202 só linha única. */
export interface TotalInvestidoPlanoMae {
  prefix: string;
  title: string;
  total: number;
  parcelCount: number;
  pctOfGrand: number;
  /** 210 e 211: expandir mostra os grupos fixos; demais: clique na linha abre pagamentos */
  expandable: boolean;
  buckets: TotalInvestidoPlanoBucket[];
  /** Capítulos sem subdivisão (207, 201, 202): todas as parcelas do prefixo */
  directItems: OutcomeItem[];
}

function resolvePlanLabelForHierarchy(
  row: FinancialPlanBreakdownRow,
  paymentCatMap: Map<string, string>,
): string {
  if (row.accountId) {
    const fromApi = paymentCatMap.get(row.accountId);
    if (fromApi) return fromApi;
    if (/^\d+$/.test(row.accountId)) {
      const alt = paymentCatMap.get(String(Number(row.accountId)));
      if (alt) return alt;
    }
  }
  return row.label;
}

const PLANO_MAE_TITLES: Record<string, string> = {
  "210": "OBRA — (2.10)",
  "211": "DESPESAS ADMINISTRATIVAS OBRA — (2.11)",
  "207": "DESPESAS TRIBUTÁRIAS — (2.07)",
  "201": "DESPESAS COM PESSOAL — (2.01)",
  "202": "DESPESAS ADMINISTRATIVAS — (2.02)",
};

const PREFIX_ORDER_SET = new Set<string>(TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED);

function breakdownRowChapterPrefix(
  row: FinancialPlanBreakdownRow,
  categoryCodesById: Map<string, string>
): string | null {
  if (row.items.length > 0) {
    return getTotalInvestidoPlanoPrefix(row.items[0], categoryCodesById);
  }
  const d =
    (row.accountId ?? "").replace(/\D/g, "") || (row.accountCode ?? "").replace(/\D/g, "");
  if (d.length < 3) return null;
  const p = d.slice(0, 3);
  return PREFIX_ORDER_SET.has(p) ? p : null;
}

/**
 * Agrupa o breakdown sob as contas mãe 210, 211, 207, 201, 202.
 * 210/211 usam buckets fixos (planilha); 207/201/202 são linha única.
 */
export function buildTotalInvestidoPlanoHierarchy(
  outcome: OutcomeItem[],
  categoryCodesById: Map<string, string>,
  paymentCatMap: Map<string, string>,
): TotalInvestidoPlanoMae[] {
  const grand = outcome.reduce((s, i) => s + i.originalAmount, 0);
  const flat = aggregateOutcomeByFinancialPlan(outcome, categoryCodesById);

  const byPrefix = new Map<string, FinancialPlanBreakdownRow[]>();
  for (const p of TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED) {
    byPrefix.set(p, []);
  }

  for (const row of flat) {
    const prefix = breakdownRowChapterPrefix(row, categoryCodesById);
    if (!prefix) continue;
    byPrefix.get(prefix)!.push(row);
  }

  const result: TotalInvestidoPlanoMae[] = [];
  for (const prefix of TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED) {
    const children = (byPrefix.get(prefix) ?? []).sort((a, b) => b.total - a.total);
    const total = children.reduce((s, c) => s + c.total, 0);
    const parcelCount = children.reduce((s, c) => s + c.parcelCount, 0);
    const pctOfGrand = grand > 0 ? (total / grand) * 100 : 0;

    const analyticInputs = children.map((r) => ({
      label: resolvePlanLabelForHierarchy(r, paymentCatMap),
      items: r.items,
    }));

    if (prefix === "210") {
      result.push({
        prefix,
        title: PLANO_MAE_TITLES[prefix] ?? `Plano ${prefix}`,
        total,
        parcelCount,
        pctOfGrand,
        expandable: true,
        buckets: buildObraBuckets(analyticInputs, grand),
        directItems: [],
      });
    } else if (prefix === "211") {
      result.push({
        prefix,
        title: PLANO_MAE_TITLES[prefix] ?? `Plano ${prefix}`,
        total,
        parcelCount,
        pctOfGrand,
        expandable: true,
        buckets: buildAdmObraBuckets(analyticInputs, grand),
        directItems: [],
      });
    } else {
      const directItems = [...children.flatMap((c) => c.items)].sort((a, b) => {
        const bd = (b.paymentDate || b.dueDate || b.issueDate || "").localeCompare(
          a.paymentDate || a.dueDate || a.issueDate || "",
        );
        if (bd !== 0) return bd;
        return b.originalAmount - a.originalAmount;
      });
      result.push({
        prefix,
        title: PLANO_MAE_TITLES[prefix] ?? `Plano ${prefix}`,
        total,
        parcelCount,
        pctOfGrand,
        expandable: false,
        buckets: [],
        directItems,
      });
    }
  }

  return result.filter((m) => m.total > 0);
}
